import { motion } from 'motion/react';
import { Wifi } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ConnectingScreenProps {
  onRetry?: () => void;
}

export function ConnectingScreen({ onRetry }: ConnectingScreenProps) {
  const { t } = useTranslation();

  return (
    <div className="min-h-dvh flex items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-gray-100">
      <motion.div
        className="max-w-sm w-full bg-white rounded-2xl shadow-lg p-8 text-center"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Pulsing wifi icon */}
        <motion.div
          className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary-100 flex items-center justify-center"
          animate={{ scale: [1, 1.08, 1] }}
          transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
        >
          <Wifi className="w-8 h-8 text-primary-600" />
        </motion.div>

        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          {t('errors.connecting')}
          <AnimatedDots />
        </h2>

        <p className="text-sm text-gray-500 mb-6">
          {t('errors.connectingMessage')}
        </p>

        {/* Subtle progress bar */}
        <div className="relative h-1 bg-gray-100 rounded-full overflow-hidden">
          <motion.div
            className="absolute inset-y-0 left-0 bg-primary-500 rounded-full"
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{ duration: 15, ease: 'linear' }}
          />
        </div>

        <button
          onClick={onRetry}
          className="mt-5 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
        >
          {t('errors.retryNow')}
        </button>
      </motion.div>
    </div>
  );
}

function AnimatedDots() {
  return (
    <span className="inline-flex w-6 ms-0.5">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="text-primary-600"
          animate={{ opacity: [0, 1, 0] }}
          transition={{
            repeat: Infinity,
            duration: 1.2,
            delay: i * 0.3,
            ease: 'easeInOut',
          }}
        >
          .
        </motion.span>
      ))}
    </span>
  );
}
